import { findOffset, arrowWidth, getHeight } from './defs';

const COLOR = '#333';
const VIDEO_COLOR = '#fff';
const MARGIN_ONLINE = 16;
const MARGIN_PRINT = 8;

export function postRender(highchart, options) {
  const [labels, shapes] = getAnnotationEls(highchart.container);
  spaceOutAndDrawLines(highchart, options, labels, shapes);
}

function getAnnotationEls(container) {
  const findByType = searchContainer(container);
  return ['label', 'shape'].map(findByType).map(sortEls);
}

function searchContainer(container) {
  return function(type) {
    const els = container.querySelectorAll(`.highcharts-annotation-${type}s`);
    return [...els[0].children];
  }
}

function sortEls(els) {
  // labels/shapes both sorted by x ascending
  return els.sort((a, b) => findAttr(a) - findAttr(b));
}

function findAttr(el) {
  const type = el.tagName;
  switch(type) {
    case 'g': // label
      const labelX = parseTransform(el).x;
      const labelWidth = getWidth(el);
      return labelX + labelWidth / 2;
    case 'circle': // circle/dot line ends
      return +el.getAttribute('cx');
    case 'path': // arrow line end
      return getArrowPosition(el).x;
  }
}

function parseTransform(label) {
  // get label's x, y
  const transform = label.getAttribute('transform');
  const start = transform.indexOf('(') + 1;
  const end = transform.indexOf(')');
  const nums = transform.slice(start, end).split(',');
  return { x: +nums[0], y: +nums[1] };
}

function getWidth(el) {
  return el.getBoundingClientRect().width;
}

function getArrowPosition(path) {
  // get arrow's x, y
  const d = path.getAttribute('d').split(' ');
  return { x: +d[4], y: +d[5] };
}

function spaceOutAndDrawLines(highchart, options, labels, shapes) {
  const [recession, suffix] = getEls(highchart);
  const margin = options.print ? MARGIN_PRINT : MARGIN_ONLINE;
  const x = getLeftX(highchart, options, margin);
  const args = [labels, shapes, x, margin, suffix, recession];
  shiftLabelsRight(...args);
  shiftLabelsLeft(options, highchart, ...args);
}

function getEls(highchart) {
  const el = getEl(highchart.container);
  const recessionEl = el('.label-recession');
  const suffixEl = el('.y-axis-suffix');
   const recession = recessionEl ?
    {
      type: 'recession',
      startX: getStartX(recessionEl),
      endX: getStartX(recessionEl) + getWidth(recessionEl),
      midpoint: getStartX(recessionEl) + getWidth(recessionEl) / 2
    }
    :
    null;
  const suffix = suffixEl ?
    {
      type: 'suffix',
      startX: getStartX(suffixEl),
      endX: getStartX(suffixEl) + getWidth(suffixEl)
    }
    :
    null;
  return [recession, suffix];
}

function getEl(container) {
  return function(tag) {
    return container.querySelector(tag);
  }
}

function getStartX(el) {
  return +el.getAttribute('x');
}

function getLeftX(highchart, options, margin) {
  if (highchart.axes[1].opposite) return 0; // y axis on right
  const shortSuffix = options.axes.units.suffix &&
    options.axes.units.suffix.length <= 2;
  if (shortSuffix) {
    const suffixEl = highchart.container.querySelector('.y-axis-suffix');
    return +suffixEl.getAttribute('x') + getWidth(suffixEl) + margin;
  }
  return topLabelX(highchart, options) + margin;
}

function topLabelX(highchart, options) {
  const reversed = options.axes.reversed;
  const yAxis = highchart.axes[1];
  const tickPositions = yAxis.tickPositions;
  const tickPosition = reversed ?
    tickPositions[0] : lastItem(tickPositions);
  return yAxis.ticks[tickPosition].label.xy.x;
}

function lastItem(arr) {
  return arr[arr.length - 1];
}

function shiftLabelsRight(labels, shapes, hardLeft, margin, suffix, recession) {
  // from left to right, ensure proper spacing between labels
  let startX = hardLeft;

  labels.forEach((label, i) => {
    const shape = shapes[i];
    const labelWidth = getWidth(label);
    let { x } = parseTransform(label),
      needTransform = false;

    if (needTransform = x < startX) x = startX;

    if (suffix || recession) {
      const { moveX, shapeX } = getPositions(x, shape, labelWidth);
      const [shiftRight, overlap] = needShiftRight(moveX, suffix, recession);
      if (shiftRight) {
        x = shiftRightX(overlap, shapeX, labelWidth);
        needTransform = true;
      }
    }

    if (needTransform) updateTransform(label, x);

    startX = x + labelWidth + margin; // farthest left next label can be
  });
}

function getPositions(x, shape, labelWidth) {
  const shapeX = getShapeX(shape);
  const labelX = x + labelWidth / 2; // label center
  const jointed = isJointed(labelX, shapeX, labelWidth);
  const moveX = jointed ? labelX : shapeX; // line top
  return { labelX, moveX, shapeX, jointed };
}

function getShapeX(shape) {
  return shape.tagName === 'circle' ?
    +shape.getAttribute('cx') : getArrowPosition(shape).x;
}

function isJointed(labelX, shapeX, labelWidth) {
  // draw jointed line only if shapeX outside label width
  return Math.abs(labelX - shapeX) >= labelWidth / 2;
}

function needShiftRight(moveX, suffix, recession) {
  const overlap = checkOverlap(moveX, recession, suffix);
  const shiftRight = overlap.type === 'suffix' ||
    overlap.type === 'recession' && moveX >= recession.midpoint;
  return [shiftRight, overlap];
}

function checkOverlap(moveX, recession, suffix) {
  // does line overlap with recession or suffix label
  if (recession && moveX >= recession.startX && moveX <= recession.endX) {
    return recession;
  }
  else if (suffix && moveX >= suffix.startX && moveX <= suffix.endX) {
    return suffix;
  }
  else {
    return { type: 'none' };
  }
}

function shiftRightX(overlap, shapeX, labelWidth) {
  // position label to ensure line wraps around recession/suffix
  const centeredOnOverlap = overlap.startX + 2 - labelWidth / 2;
  return shapeX >= centeredOnOverlap && shapeX <= overlap.endX ?
    shapeX + 1 : centeredOnOverlap;
}

function updateTransform(label, x) {
  // set new x for label
  const { y } = parseTransform(label);
  const translate = `translate(${x},${y})`;
  label.setAttribute('transform', translate);
}

function shiftLabelsLeft(options, highchart, labels, shapes, hardLeft, margin, suffix, recession) {
  /*
   * From right to left, ensure proper spacing between labels
   * After positioning each label, draw its line
   * Hide annotations pushed off left of chart or into suffix label
  */
  const wsjPrint = options.print && options.product === 'wsj';
  const offset = wsjPrint ? 1 : 0;
  const hardRight = highchart.plotLeft + highchart.plotWidth - offset;
  
  const drawLine = lineRenderer(highchart, options, margin);

  let startX = hardRight, nextLabel, nextShape;
  const lastLabel = labels.length - 1;  
  
  for (let i = lastLabel; i >= 0; i--) {
    
    const label = labels[i], shape = shapes[i];
    const labelWidth = getWidth(label);
    const [flushAlign, showLine, dotted] = getProps(label);
    let { x } = parseTransform(label), transformX;

    if (x + labelWidth >= startX) {
      x = startX - labelWidth;
      const itemFlush = i === lastLabel && flushAlign;
      // for right-most labels pushed off chart with flush alignment selected
      if (itemFlush) label.setAttribute('text-anchor', 'end');
      transformX = itemFlush ? hardRight : x;
    }
  
    if (recession || suffix) {
      const { moveX, shapeX } = getPositions(x, shape, labelWidth);
      const overlap = checkOverlap(moveX, recession, suffix);
      if (overlap.type !== 'none') {
        x = shiftLeftX(overlap, shapeX, labelWidth);
        transformX = x;
      }
    }
    
    const positions = getPositions(x, shape, labelWidth);
    
    const hidden = x < hardLeft ||
      options.product !== 'financialnews' && suffix && positions.moveX <= suffix.endX;
    hidden ?
      hideAnnotation(label, shape)
      :
      transformX && updateTransform(label, transformX);
    
    if (showLine && !hidden) {
      drawLine(label, shape, nextLabel, nextShape, positions, dotted);
    }
    
    [nextLabel, nextShape] = [positions.moveX, positions.shapeX]; // to determine whether jointed lines overlap
    startX = x - margin; // farthest right next label can be
  }
}

function lineRenderer(highchart, options, margin) {
  const cushion = margin / 2;
  let addPx = cushion;
  return function(label, shape, nextLabel, nextShape, positions, dotted) {
    const { labelX, moveX, shapeX, jointed } = positions;
    const [labelY, shapeY] = [getLabelY(label), adjustShapeY(highchart, options, shape)];
    
    // for highstocks charts, if shape cut off on left, re-render shape to force added space
    if (options.irregularDates) checkCutoff(highchart, options, shape, shapeX, shapeY);

    const [m, jointPath, lastL] = getPathStart(moveX, labelY, shapeX, shapeY);
    if (jointed) {
      // draw jointed line
      const jointRight = labelX > shapeX;
      let jointY = initialJointY(highchart, margin, jointRight);
      // if jointed lines overlap, bump 2nd line down
      if (nextLabel) {
        if (shapeX >= nextLabel) {
          jointY += addPx;
          addPx += cushion; // keep increasing addPx if >2 consecutive jointed lines overlap
        } else if (moveX >= nextShape) {
          jointY -= addPx;
          addPx += cushion;
        } else {
          addPx = cushion; // reset addPx once consecutive jointed lines don't overlap
        }
      }
      jointPath.push(...['L', labelX, jointY, 'L', shapeX, jointY]);
    }
    const path = [...m, ...jointPath, ...lastL];
    renderPath(highchart, options, path, dotted);
  }
}

function getLabelY(label) {
  // line top positioned below label
  const { y } = parseTransform(label);
  const labelHeight = label.getBoundingClientRect().height;
  return y + labelHeight;
}

function adjustShapeY(highchart, options, shape) {
  let shapeY = shape.tagName === 'circle' ?
    +shape.getAttribute('cy') : getArrowPosition(shape).y;
  if (!shapeY) { // check whether category enabled
    const xAxisPathPoints = highchart.axes[0].axisLine.d.split(' ');
    shapeY = +lastItem(xAxisPathPoints); // line extends to xAxis
  }
  return adjustLineEnd(options, shape, shapeY);
}

function adjustLineEnd(options, shape, shapeY) {
  // for arrow/circle line ends, shorten line for proper intersection with shape
  const isCircle = +shape.getAttribute('stroke-width') > 0;
  let offsetY = 0;
  if (isCircle) {
    offsetY = +shape.getAttribute('r');
  } else if (shape.tagName === 'path') { // arrow
    offsetY = findOffset(options);
  }
  return shapeY - offsetY;
}

function checkCutoff(highchart, options, shape, shapeX, shapeY) {
  if (+shape.getAttribute('stroke-width') === 0) {
    const isDot = shape.tagName === 'circle';
    const width = arrowWidth(options.print);
    const midpoint = width / 2;
    const r = isDot ? +shape.getAttribute('r') : midpoint;
    
    if (shapeX - r <= highchart.plotLeft) {
      shape.setAttribute('display', 'none');
      const fill = options.id === 'video' ? VIDEO_COLOR : COLOR;
      const attr = { fill, zIndex: 4 };
      
      if (isDot) {
        highchart.renderer.circle(shapeX, shapeY, r)
        .attr(attr)
        .add();
      }      
      else { // arrow
        const height = getHeight(width, midpoint);
        const path = ['M', shapeX, shapeY, 'L', shapeX + midpoint, shapeY, 'L', shapeX, shapeY + height, 'L', shapeX - midpoint, shapeY, 'z'];
        
        highchart.renderer.path(path)
        .attr(attr)
        .add();
      }
    }
  }
}

function getPathStart(moveX, labelY, shapeX, shapeY) {
  // top, bottom of line
  return [['M', moveX, labelY], [], ['L', shapeX, shapeY]];
}

function initialJointY(highchart, margin, jointRight) {
  const divisor = jointRight ? 1 : 2;
  return highchart.plotTop + margin / divisor;
}

function renderPath(highchart, options, path, dotted) {
  const { print, id } = options;
  const strokeWidth = getStrokeWidth(print, dotted);
  const gap = print ? 3 : 5;
  const dashArray = dotted ? `0.1,${gap}` : undefined;
  highchart.renderer.path(path)
    .attr({
      stroke: id === 'video' ? VIDEO_COLOR : COLOR,
      'stroke-width': strokeWidth,
      'stroke-dasharray': dashArray,
      'stroke-linecap': 'round',
      zIndex: 4
    })
    .add()
}

function getStrokeWidth(print, dotted) {
  if (print && dotted) return 1.5;
  else if (print && !dotted) return 0.5;
  else if (!print && dotted) return 2.5;
  else return 1;
}

function getProps(label) {
  // get properties set in prerender
  const styles = label.getAttribute('style').split(';');
  const flushAlign = styles[0];
  const showLine = styles[1];
  const lineType = styles[2];
  return [
    !flushAlign.includes('default'),
    showLine.includes('true') || showLine.includes('undefined'),
    lineType.includes('dotted')
  ];
}

function shiftLeftX(overlap, shapeX, labelWidth) {
  // position label to ensure line wraps around recession/suffix
  const centeredOnOverlap = overlap.startX - 2 - labelWidth / 2;
  return shapeX <= centeredOnOverlap + labelWidth && shapeX >= overlap.startX ?
    shapeX - labelWidth - 1 : centeredOnOverlap;
}

function hideAnnotation(...args) {
  args.forEach(arg => arg.setAttribute('display', 'none'));
}



// WEBPACK FOOTER //
// ./src/chart/inner/annotations-postrender.js